import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  adminShortlinksList,
  adminShortlinkCreate,
  adminShortlinkDelete,
  adminShortlinkClicks,
  adminShortlinkCheckSlug,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Link as LinkIcon, Copy, Check, Trash2, BarChart2, Plus, ExternalLink, RefreshCcw, Search,
} from 'lucide-react';

const FLAGS = {
  BD: '\u{1F1E7}\u{1F1E9}', IN: '\u{1F1EE}\u{1F1F3}', PK: '\u{1F1F5}\u{1F1F0}',
  US: '\u{1F1FA}\u{1F1F8}', GB: '\u{1F1EC}\u{1F1E7}', DE: '\u{1F1E9}\u{1F1EA}',
  AE: '\u{1F1E6}\u{1F1EA}', SA: '\u{1F1F8}\u{1F1E6}', BR: '\u{1F1E7}\u{1F1F7}',
  MV: '\u{1F1F2}\u{1F1FB}', ID: '\u{1F1EE}\u{1F1E9}', PH: '\u{1F1F5}\u{1F1ED}',
  TR: '\u{1F1F9}\u{1F1F7}', EG: '\u{1F1EA}\u{1F1EC}', MY: '\u{1F1F2}\u{1F1FE}',
};

function CopyButton({ text, label = 'short link' }) {
  const [done, setDone] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success(`${label} copied`);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={copy}
      className="h-8 px-2 border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
      data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {done ? <Check className="h-3.5 w-3.5 text-[hsl(152_78%_55%)]" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function truncate(s, n = 55) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso.slice(0, 16).replace('T', ' '); }
}

export default function AdminShortlinksTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [slugStatus, setSlugStatus] = useState(null); // { available, reason, message } | null
  const [slugChecking, setSlugChecking] = useState(false);
  const [search, setSearch] = useState('');
  const [statsOpen, setStatsOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminShortlinksList();
      setItems(d.items || []);
    } catch {
      toast.error('Failed to load shortlinks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Live slug availability check (debounced)
  useEffect(() => {
    const s = slugInput.trim();
    if (!s) { setSlugStatus(null); return; }
    // Client-side fast format check
    if (!/^[a-zA-Z0-9_-]{3,40}$/.test(s)) {
      setSlugStatus({ available: false, reason: 'format', message: '3–40 chars: letters, digits, - or _' });
      return;
    }
    setSlugChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await adminShortlinkCheckSlug(s);
        setSlugStatus(r);
      } catch {
        setSlugStatus(null);
      } finally {
        setSlugChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [slugInput]);

  const totals = useMemo(() => {
    const clicks = items.reduce((a, r) => a + (r.clicks || 0), 0);
    const uniq = items.reduce((a, r) => a + (r.unique_clicks || 0), 0);
    return { total: items.length, clicks, uniq };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => {
      const hay = `${r.slug || ''} ${r.url || ''} ${r.note || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  async function onCreate(e) {
    e.preventDefault();
    const v = urlInput.trim();
    if (!v) { toast.error('Please enter a URL'); return; }
    const customSlug = slugInput.trim();
    if (customSlug && slugStatus && !slugStatus.available) {
      toast.error(`Slug not usable: ${slugStatus.message}`);
      return;
    }
    setCreating(true);
    try {
      const created = await adminShortlinkCreate(v, noteInput.trim() || null, customSlug || null);
      toast.success(`Short link created: ${created.slug}`);
      setUrlInput('');
      setNoteInput('');
      setSlugInput('');
      setSlugStatus(null);
      await refresh();
      // Try to copy the new short URL right away (best-effort)
      try { await navigator.clipboard.writeText(created.short_url); toast.message('Short URL copied to clipboard'); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create short link');
    } finally {
      setCreating(false);
    }
  }

  async function onDelete(slug) {
    try {
      await adminShortlinkDelete(slug);
      toast.success(`Deleted /s/${slug}`);
      await refresh();
    } catch {
      toast.error('Failed to delete');
    }
  }

  async function openStats(slug) {
    setStatsOpen(true);
    setStatsLoading(true);
    setStatsData(null);
    try {
      setStatsData(await adminShortlinkClicks(slug));
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setStatsLoading(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="admin-shortlinks-tab">
      {/* Create Form */}
      <Card className="card-surface" data-testid="shortlink-create-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Create Short Link
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Paste any long URL — we'll mint a clean <code className="font-mono">alveetrade.com/s/xxxxxx</code> link
            and track clicks.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onCreate} className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Destination URL</Label>
              <Input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/some/very/long/path?with=params"
                className="mt-1 bg-white/[0.02] border-white/10 font-mono text-sm"
                data-testid="shortlink-url-input"
                required
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Custom slug <span className="text-muted-foreground/60 lowercase tracking-normal normal-case">(optional — blank = auto)</span>
                </Label>
                <div className="relative mt-1">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-xs text-muted-foreground font-mono">
                    alveetrade.com/s/
                  </div>
                  <Input
                    type="text"
                    value={slugInput}
                    onChange={(e) => setSlugInput(e.target.value)}
                    placeholder="my-offer"
                    maxLength={40}
                    className={`bg-white/[0.02] border-white/10 font-mono text-sm pl-[140px] ${
                      slugStatus && !slugStatus.available
                        ? 'border-[rgba(225,29,46,0.5)] focus-visible:ring-[hsl(0_72%_55%)]'
                        : slugStatus && slugStatus.available
                        ? 'border-[rgba(34,199,132,0.5)]'
                        : ''
                    }`}
                    data-testid="shortlink-slug-input"
                  />
                </div>
                <div className="mt-1 text-[11px] min-h-[14px]">
                  {slugChecking && <span className="text-muted-foreground">Checking…</span>}
                  {!slugChecking && slugStatus && slugStatus.available && (
                    <span className="text-[hsl(152_78%_55%)] inline-flex items-center gap-1">
                      <Check className="h-3 w-3" /> Available · will create <code className="font-mono">alveetrade.com/s/{slugInput.trim()}</code>
                    </span>
                  )}
                  {!slugChecking && slugStatus && !slugStatus.available && (
                    <span className="text-[hsl(0_72%_60%)]">{slugStatus.message}</span>
                  )}
                  {!slugInput.trim() && (
                    <span className="text-muted-foreground">Leave blank for auto-generated 6-char slug.</span>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Note (optional)</Label>
                <Input
                  type="text"
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  placeholder="e.g. TikTok July campaign"
                  className="mt-1 bg-white/[0.02] border-white/10"
                  maxLength={200}
                  data-testid="shortlink-note-input"
                />
                <div className="mt-1 text-[11px] text-muted-foreground min-h-[14px]">
                  Private label — only visible to admin.
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                type="submit"
                disabled={creating || (!!slugInput.trim() && slugStatus && !slugStatus.available)}
                className="cta-sheen text-white border-0 gap-2"
                data-testid="shortlink-create-btn"
              >
                <Plus className="h-4 w-4" /> {creating ? 'Creating…' : 'Create short link'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Totals */}
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="Total Links" value={totals.total} testid="kpi-shortlinks-total" />
        <StatTile label="All Clicks" value={totals.clicks} testid="kpi-shortlinks-clicks" />
        <StatTile label="Unique Clicks" value={totals.uniq} testid="kpi-shortlinks-uniq" />
      </div>

      {/* List */}
      <Card className="card-surface" data-testid="shortlink-list-card">
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">All Short Links</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search slug / URL / note"
                className="h-9 pl-7 w-[220px] bg-white/[0.02] border-white/10 text-xs"
                data-testid="shortlink-search"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={refresh}
              disabled={loading}
              className="h-9 border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white gap-2"
              data-testid="shortlink-refresh"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Short URL</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                  <TableHead className="text-right">Unique</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.slug} data-testid={`shortlink-row-${r.slug}`}>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-[hsl(43_92%_56%)]">alveetrade.com/s/{r.slug}</span>
                        <CopyButton text={r.short_url} label={r.slug} />
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-[hsl(0_0%_85%)]">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-white hover:underline inline-flex items-center gap-1"
                        title={r.url}
                      >
                        {truncate(r.url, 50)} <ExternalLink className="h-3 w-3" />
                      </a>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.note || '—'}</TableCell>
                    <TableCell className="text-right font-semibold text-[hsl(43_92%_56%)]">{r.clicks || 0}</TableCell>
                    <TableCell className="text-right text-xs">{r.unique_clicks || 0}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openStats(r.slug)}
                          className="h-8 px-2 border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                          data-testid={`shortlink-stats-${r.slug}`}
                          title="View click analytics"
                        >
                          <BarChart2 className="h-3.5 w-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.05)] text-[hsl(0_72%_60%)] hover:bg-[rgba(225,29,46,0.12)] hover:text-[hsl(0_72%_70%)]"
                              data-testid={`shortlink-delete-${r.slug}`}
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete short link?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes <code className="font-mono">/s/{r.slug}</code> and its click history.
                                Any existing references to this short URL will start returning 404.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(r.slug)}
                                className="bg-[hsl(0_72%_50%)] hover:bg-[hsl(0_72%_45%)] text-white"
                                data-testid={`shortlink-delete-confirm-${r.slug}`}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground text-sm py-8">
                      {items.length === 0
                        ? 'No short links yet. Create your first one above.'
                        : 'No links match your search.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Stats Dialog */}
      <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
        <DialogContent className="max-w-2xl bg-[hsl(240_6%_9%)] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart2 className="h-4 w-4 text-[hsl(43_92%_56%)]" />
              {statsData?.link?.slug ? `Analytics · /s/${statsData.link.slug}` : 'Analytics'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {statsData?.link?.url ? <span className="break-all">{statsData.link.url}</span> : 'Loading…'}
            </DialogDescription>
          </DialogHeader>
          {statsLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading analytics…</div>
          ) : statsData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <StatTile label="Total Clicks" value={statsData.link.clicks || 0} mono />
                <StatTile label="Unique" value={statsData.link.unique_clicks || 0} mono />
                <StatTile label="Created" value={fmtDate(statsData.link.created_at)} mono small />
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">By Country · Top 10</div>
                {(statsData.by_country || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No country data yet.</div>
                ) : (
                  <div className="rounded-md border border-white/10 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Country</TableHead>
                          <TableHead className="text-right">Clicks</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {statsData.by_country.slice(0, 10).map((c) => (
                          <TableRow key={c.country_code}>
                            <TableCell className="font-medium">
                              {FLAGS[c.country_code] || '🌐'} {c.country_name || c.country_code}
                            </TableCell>
                            <TableCell className="text-right">{c.clicks}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Recent Clicks · Last 20</div>
                {(statsData.recent || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No clicks yet.</div>
                ) : (
                  <div className="rounded-md border border-white/10 overflow-hidden max-h-[280px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Country</TableHead>
                          <TableHead>Referrer</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {statsData.recent.slice(0, 20).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                            <TableCell className="text-xs">{FLAGS[r.country_code] || ''} {r.country_code || '??'}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{truncate(r.referrer || 'direct', 40)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, mono = false, small = false, testid }) {
  return (
    <div className="rounded-md border border-border bg-white/[0.02] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`mt-1 text-[hsl(43_92%_56%)] ${mono ? 'font-mono' : 'font-display'} ${small ? 'text-sm' : 'text-2xl'}`}>
        {value}
      </div>
    </div>
  );
}
